import React from "react";

function OrderSummary({
  cartItems,
  totalCartPrice,
  checkoutStep,
  handleProductClick,
  handleUpdateCartQuantity,
  handleUpdatePairsToShip,
  handleRemoveFromCart,
  shippingCost,
  processingFee,
  taxAmount,
  totalWithFee,
  paymentIntentReady,
  showShippingInfo,
  setShowShippingInfo,
}) {
  // Step 1: Cart review
  if (checkoutStep === 1) {
    return (
      <div>
        <h3 className="text-2xl font-bold mb-6">Items In Cart</h3>
        {cartItems.length === 0 ? (
          <p className="text-lg text-gray-500">Your cart is empty.</p>
        ) : (
          <ul>
            {cartItems.map((item, idx) => (
              <React.Fragment key={idx}>
                <li className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                  <div className="flex items-center space-x-4 w-full">
                    {item.images && item.images.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt={item.title || item.name}
                        className="w-16 h-16 object-cover rounded-xl border cursor-pointer"
                        onClick={() => handleProductClick(item)}
                      />
                    )}
                    <span
                      className="text-2xl cursor-pointer"
                      onClick={() => handleProductClick(item)}
                    >
                      {item.title || item.name}
                    </span>
                    {item.stock > 1 && (
                      <div className="flex flex-col items-center space-y-1 ml-4 w-full">
                        <div className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-center">
                          <div className="flex flex-col items-center w-full sm:w-auto mb-2 sm:mb-0">
                            <label className="text-sm font-semibold mb-1 text-center">
                              Quantity:
                            </label>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                className="px-3 py-1 rounded-full bg-gray-200 text-xl font-bold hover:bg-gray-300 transition"
                                onClick={() =>
                                  handleUpdateCartQuantity(
                                    idx,
                                    Math.max(1, item.quantity - 1)
                                  )
                                }
                                aria-label="Decrease quantity"
                              >
                                −
                              </button>
                              <span className="w-10 text-center text-lg font-semibold">
                                {item.quantity || 1}
                              </span>
                              <button
                                type="button"
                                className="px-3 py-1 rounded-full bg-gray-200 text-xl font-bold hover:bg-gray-300 transition"
                                onClick={() =>
                                  handleUpdateCartQuantity(
                                    idx,
                                    Math.min(item.stock, item.quantity + 1)
                                  )
                                }
                                aria-label="Increase quantity"
                              >
                                +
                              </button>
                            </div>
                            <span className="text-sm text-gray-500 font-bold mt-2">
                              {item.stock} available
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    {item.stock === 1 && (
                      <span className="text-sm font-semibold mb-1">
                        Quantity: 1
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-4 mt-2 sm:mt-0 sm:self-auto">
                    <span className="text-lg">
                      ${(item.price / 100).toFixed(2)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFromCart(idx)}
                      className="ml-2 p-3 bg-transparent hover:bg-red-100 rounded-full"
                      aria-label="Remove from cart"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide-icon text-red-500"
                      >
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m5 0V4a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" x2="10" y1="11" y2="17"></line>
                        <line x1="14" x2="14" y1="11" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </li>
                {cartItems.length > 1 && idx !== cartItems.length - 1 && (
                  <div className="w-full flex">
                    <div className="flex items-center w-full px-0">
                      <div
                        className="border-t border-gray-300 w-full my-4"
                        style={{ borderWidth: "1px" }}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </ul>
        )}
        {cartItems.length > 0 && (
          <div className="mt-6 flex flex-col items-end space-y-2">
            <div className="text-right font-semibold text-lg flex items-center mt-4">
              <span className="ml-0">Cart Subtotal:</span>
              <span className="font-normal ml-2">
                ${(totalCartPrice / 100).toFixed(2)}
              </span>
            </div>
            <div className="text-right font-semibold text-lg flex items-center relative group">
              <span>Shipping Total:</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowShippingInfo(true);
                }}
                className="ml-2 p-1 rounded-full hover:bg-gray-200 transition"
                aria-label="Shipping information"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-600 hover:text-indigo-600"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </button>
              <span className="font-normal ml-2">{"Calculated on step 4"}</span>
            </div>
            <div className="text-right font-semibold text-lg">
              Payment Processing Fee (2.9%):{" "}
              <span className="font-normal">{"Calculated on step 4"}</span>
            </div>
            <div className="text-right font-semibold text-lg">
              Sales Tax (If applicable):{" "}
              <span className="font-normal">{"Calculated on step 4"}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Step 4: Payment summary
  const showFinals = checkoutStep === 4 && paymentIntentReady;

  return (
    <div>
      <h3 className="text-2xl font-bold mb-6">Order Summary</h3>
      {cartItems.length === 0 ? (
        <p className="text-lg text-gray-500">Your cart is empty.</p>
      ) : (
        <ul>
          {cartItems.map((item, idx) => (
            <React.Fragment key={idx}>
              <li className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div className="flex items-center space-x-4 w-full">
                  {item.images && item.images.length > 0 && (
                    <img
                      src={item.images[0]}
                      alt={item.title || item.name}
                      className="w-16 h-16 object-cover rounded-xl border cursor-pointer"
                      onClick={() => handleProductClick(item)}
                    />
                  )}
                  <span
                    className="text-2xl cursor-pointer"
                    onClick={() => handleProductClick(item)}
                  >
                    {item.title || item.name}
                  </span>
                  {item.stock > 1 && (
                    <div className="flex flex-col items-center space-y-1 ml-4 w-full">
                      <div className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-center">
                        <div className="flex flex-col items-center w-full sm:w-auto mb-2 sm:mb-0">
                          <label className="text-sm font-semibold mb-1 text-center">
                            Quantity:
                          </label>
                          <div className="flex items-center space-x-2">
                            <span className="w-10 text-center text-lg font-semibold">
                              {item.quantity || 1}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500 font-bold mt-2">
                            {item.stock} available
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {item.stock === 1 && (
                    <span className="text-sm font-semibold mb-1">
                      Quantity: 1
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-4 mt-2 sm:mt-0 sm:self-auto">
                  <span className="text-lg">
                    ${(item.price / 100).toFixed(2)}
                  </span>
                </div>
              </li>
              {cartItems.length > 1 && idx !== cartItems.length - 1 && (
                <div className="w-full flex">
                  <div className="flex items-center w-full px-0">
                    <div
                      className="border-t border-gray-300 w-full my-4"
                      style={{ borderWidth: "1px" }}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </ul>
      )}
      <div className="mt-6 flex flex-col items-end space-y-2">
        <div className="text-right font-semibold text-lg flex items-center mt-4">
          <span className="ml-0">Cart Subtotal:</span>
          <span className="font-normal ml-2">
            ${(totalCartPrice / 100).toFixed(2)}
          </span>
        </div>

        {/* Shipping Total with Info Icon */}
        <div className="text-right font-semibold text-lg flex items-center relative group">
          <span>Shipping Total:</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowShippingInfo(true);
            }}
            className="ml-2 p-1 rounded-full hover:bg-gray-200 transition"
            aria-label="Shipping information"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-600 hover:text-indigo-600"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </button>
          <span className="font-normal ml-2">
            {showFinals && typeof shippingCost === "number"
              ? `$${(shippingCost / 100).toFixed(2)}`
              : checkoutStep === 4
              ? "—"
              : typeof shippingCost === "number"
              ? `$${(shippingCost / 100).toFixed(2)}`
              : "—"}
          </span>
        </div>
        <div className="text-right font-semibold text-lg">
          Payment Processing Fee (2.9%):{" "}
          <span className="font-normal">
            {showFinals && typeof processingFee === "number"
              ? `$${(processingFee / 100).toFixed(2)}`
              : checkoutStep === 4
              ? "—"
              : typeof processingFee === "number"
              ? `$${(processingFee / 100).toFixed(2)}`
              : "—"}
          </span>
        </div>
        <div className="text-right font-semibold text-lg">
          Sales Tax:{" "}
          <span className="font-normal">
            {showFinals && typeof taxAmount === "number"
              ? `$${(taxAmount / 100).toFixed(2)}`
              : checkoutStep === 4
              ? "—"
              : typeof taxAmount === "number"
              ? `$${(taxAmount / 100).toFixed(2)}`
              : "—"}
          </span>
        </div>
        <div className="text-right font-bold text-2xl">
          Total:{" "}
          {showFinals && typeof totalWithFee === "number"
            ? `$${(totalWithFee / 100).toFixed(2)}`
            : checkoutStep === 4
            ? "—"
            : typeof totalWithFee === "number"
            ? `$${(totalWithFee / 100).toFixed(2)}`
            : "—"}
        </div>
      </div>
    </div>
  );
}

export default OrderSummary;